import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { qrApi } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import {
  CheckCircle,
  XCircle,
  CircleNotch,
  Package,
  Calendar,
  Hash,
  Warning,
  MapPin,
  ShieldWarning,
  Factory,
  Truck,
  User,
} from '@phosphor-icons/react';
import { formatDateTime, getStatusColor } from '../lib/utils';

const LOGO_FULL = '/images/logo-full.png';

function JourneyStepIcon({ label, status }) {
  const cls = status === 'completed'
    ? 'text-green-500'
    : status === 'current'
      ? 'text-brand-500'
      : 'text-slate-300';
  const lower = (label || '').toLowerCase();
  if (lower.includes('manufactur') || lower.includes('registered')) {
    return <Factory className={`w-5 h-5 ${cls}`} />;
  }
  if (lower.includes('ship') || lower.includes('transit') || lower.includes('deliver')) {
    return <Truck className={`w-5 h-5 ${cls}`} />;
  }
  if (lower.includes('consumer')) {
    return <User className={`w-5 h-5 ${cls}`} />;
  }
  return <Package className={`w-5 h-5 ${cls}`} />;
}

function JourneyTimeline({ journey, t }) {
  if (!journey?.length) return null;

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold">
          {t('verify.productJourney')}
        </p>
      </div>
      <div className="relative space-y-0">
        {journey.map((step, index) => {
          const isLast = index === journey.length - 1;
          const dotColor = step.status === 'completed'
            ? 'bg-green-500 border-green-500'
            : step.status === 'current'
              ? 'bg-brand-500 border-brand-500 ring-4 ring-brand-100'
              : 'bg-white border-slate-300';

          return (
            <div key={`${step.step}-${step.label}`} className="flex gap-3 pb-4 relative">
              {!isLast && (
                <div
                  className={`absolute left-[11px] top-6 bottom-0 w-0.5 ${
                    step.status === 'completed' ? 'bg-green-300' : 'bg-slate-200'
                  }`}
                />
              )}
              <div className={`relative z-10 w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${dotColor}`}>
                {step.status === 'completed' && (
                  <CheckCircle weight="fill" className="w-4 h-4 text-white" />
                )}
              </div>
              <div className={`flex-1 min-w-0 pb-1 ${step.status === 'pending' ? 'opacity-50' : ''}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <JourneyStepIcon label={step.label} status={step.status} />
                    <p className={`text-sm font-medium ${
                      step.status === 'completed' ? 'text-green-800'
                        : step.status === 'current' ? 'text-brand-800'
                          : 'text-slate-500'
                    }`}>
                      {step.label}
                    </p>
                  </div>
                  {step.status === 'current' && (
                    <span className="text-[10px] uppercase tracking-wide text-brand-600 font-semibold bg-brand-50 px-1.5 py-0.5 rounded">
                      {t('verify.currentStep')}
                    </span>
                  )}
                </div>
                {step.description && (
                  <p className="text-xs text-slate-500 mt-0.5 ml-7">{step.description}</p>
                )}
                {step.timestamp && (
                  <p className="text-xs text-slate-400 mt-1 ml-7 flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {formatDateTime(step.timestamp)}
                  </p>
                )}
                {step.location && (
                  <p className="text-xs text-slate-500 mt-0.5 ml-7 flex items-center gap-1">
                    <MapPin className="w-3 h-3" />
                    {step.location}
                  </p>
                )}
                {step.actor && step.actor !== 'OMNICHEK' && step.actor !== 'Manufacturer' && (
                  <p className="text-xs text-slate-400 mt-0.5 ml-7 flex items-center gap-1">
                    <User className="w-3 h-3" />
                    {step.actor}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function VerifyPage() {
  const { t } = useTranslation();
  const { qrId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    verifyQR();
  }, [qrId]); // eslint-disable-line react-hooks/exhaustive-deps

  const verifyQR = async () => {
    try {
      let lat = null;
      let lng = null;
      try {
        const position = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 5000 });
        });
        lat = position.coords.latitude;
        lng = position.coords.longitude;
      } catch {
        // Location not available - continue without coords
      }
      const params = {};
      if (lat !== null) params.lat = lat;
      if (lng !== null) params.lng = lng;

      const response = await qrApi.verify(qrId, params);
      setData(response.data);
    } catch (err) {
      setError('Failed to verify QR code');
    } finally {
      setLoading(false);
    }
  };

  const journey = data?.journey?.length ? data.journey : null;
  const progress = data?.progress_percentage ?? 0;

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-slate-100 to-slate-200" data-testid="verify-page">
      <Card className="w-full max-w-md bg-white shadow-xl border-slate-200">
        <CardContent className="p-6">
          <div className="flex items-center justify-center mb-6">
            <img src={LOGO_FULL} alt="OMNICHEK" className="h-8 object-contain" />
          </div>

          {loading ? (
            <div className="text-center py-8">
              <CircleNotch className="w-10 h-10 text-brand-500 animate-spin mx-auto mb-4" />
              <p className="text-slate-600">{t('verify.verifying')}</p>
            </div>
          ) : error || !data?.valid ? (
            <div className="text-center py-6">
              <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${data?.counterfeit ? 'bg-red-100' : 'bg-red-50'}`}>
                <XCircle weight="fill" className="w-10 h-10 text-red-500" />
              </div>
              <h2 className="text-xl font-heading font-semibold text-slate-900 mb-2">
                {data?.counterfeit ? t('verify.counterfeitAlert') : t('verify.verificationFailed')}
              </h2>
              <p className="text-slate-500">{data?.message || t('verify.couldNotVerify')}</p>
              {data?.counterfeit && (
                <div className="mt-4 p-3 bg-red-50 rounded-lg border border-red-200">
                  <p className="text-sm text-red-700 font-medium">{t('verify.counterfeitWarning')}</p>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-5">
              {data.is_in_transit && data.stolen_warning && (
                <div className="p-4 bg-red-50 rounded-lg border-2 border-red-300" data-testid="stolen-warning">
                  <div className="flex items-start gap-3">
                    <ShieldWarning weight="fill" className="w-8 h-8 text-red-600 flex-shrink-0" />
                    <div>
                      <h3 className="text-red-800 font-bold text-sm mb-1">{t('verify.stolenWarning')}</h3>
                      <p className="text-red-700 text-xs leading-relaxed">{data.stolen_warning}</p>
                    </div>
                  </div>
                </div>
              )}

              <div className="text-center">
                {data.is_in_transit ? (
                  <>
                    <div className="w-16 h-16 rounded-full bg-brand-50 flex items-center justify-center mx-auto mb-3">
                      <Warning weight="fill" className="w-10 h-10 text-brand-500" />
                    </div>
                    <h2 className="text-lg font-heading font-semibold text-slate-900 mb-1">{t('verify.productInTransit')}</h2>
                    <p className="text-brand-600 text-sm font-medium">{t('verify.notForRetail')}</p>
                  </>
                ) : (
                  <>
                    <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-3">
                      <CheckCircle weight="fill" className="w-10 h-10 text-green-500" />
                    </div>
                    <h2 className="text-lg font-heading font-semibold text-slate-900 mb-1">{t('verify.authentic')}</h2>
                    <p className="text-green-600 text-sm">{t('verify.authenticDesc')}</p>
                  </>
                )}
              </div>

              {data.is_suspicious && data.tamper_warning && (
                <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-200 flex items-start gap-2">
                  <Warning weight="fill" className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-yellow-700">{data.tamper_warning}</p>
                </div>
              )}

              {progress > 0 && (
                <div>
                  <div className="flex justify-between text-xs text-slate-500 mb-1">
                    <span>{t('verify.supplyChainProgress')}</span>
                    <span>{progress}%</span>
                  </div>
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${data.is_in_transit ? 'bg-brand-400' : 'bg-green-500'}`}
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                </div>
              )}

              <div className="text-center">
                <span className={`inline-flex px-3 py-1.5 rounded-full text-sm font-medium border ${getStatusColor(data.status)}`}>
                  {t('verify.currentStatus')}: {data.status}
                </span>
              </div>

              {data.product && (
                <div className="space-y-3 p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <div className="flex items-center gap-3">
                    <Package className="w-5 h-5 text-brand-500" />
                    <div>
                      <p className="text-xs text-slate-400 uppercase tracking-wider">{t('verify.productInfo')}</p>
                      <p className="text-slate-900 font-medium">{data.product.name}</p>
                    </div>
                  </div>
                  {data.product.sku && (
                    <div className="flex items-center gap-3">
                      <Hash className="w-5 h-5 text-slate-400" />
                      <div><p className="text-xs text-slate-400 uppercase">SKU</p><p className="text-slate-600">{data.product.sku}</p></div>
                    </div>
                  )}
                  {data.product.batch_number && (
                    <div className="flex items-center gap-3">
                      <Hash className="w-5 h-5 text-slate-400" />
                      <div><p className="text-xs text-slate-400 uppercase">{t('verify.batch')}</p><p className="text-slate-600">{data.product.batch_number}</p></div>
                    </div>
                  )}
                  {data.product.manufacturing_date && (
                    <div className="flex items-center gap-3">
                      <Calendar className="w-5 h-5 text-slate-400" />
                      <div><p className="text-xs text-slate-400 uppercase">{t('verify.manufactured')}</p><p className="text-slate-600">{data.product.manufacturing_date}</p></div>
                    </div>
                  )}
                  {data.product.expiry_date && (
                    <div className="flex items-center gap-3">
                      <Calendar className="w-5 h-5 text-slate-400" />
                      <div><p className="text-xs text-slate-400 uppercase">{t('verify.expiry')}</p><p className="text-slate-600">{data.product.expiry_date}</p></div>
                    </div>
                  )}
                </div>
              )}

              {journey ? (
                <JourneyTimeline journey={journey} t={t} />
              ) : data.scan_stages?.length > 0 && (
                <div>
                  <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-2">{t('verify.statusTimeline')}</p>
                  <div className="space-y-1">
                    {data.scan_stages.map((stage) => {
                      const completed = data.status_history?.some(
                        (h) => h.status.toLowerCase() === stage.toLowerCase()
                          || h.status.toLowerCase() === stage.toLowerCase().replace(/ /g, '_'),
                      );
                      return (
                        <div key={stage} className={`flex items-center gap-2 p-2 rounded text-sm ${completed ? 'bg-green-50 border border-green-200' : 'bg-slate-50 border border-slate-200'}`}>
                          {completed ? (
                            <CheckCircle weight="fill" className="w-4 h-4 text-green-500" />
                          ) : (
                            <div className="w-4 h-4 rounded-full border-2 border-slate-300" />
                          )}
                          <span className={completed ? 'text-green-700' : 'text-slate-500'}>{stage}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {data.total_scans && (
                <div className="text-center text-xs text-slate-400">
                  <MapPin className="w-3 h-3 inline mr-1" />
                  {t('verify.scanCount')}: #{data.total_scans}
                </div>
              )}

              <div className="text-center pt-3 border-t border-slate-200">
                <p className="text-xs text-slate-400">QR ID</p>
                <p className="text-xs text-slate-500 font-mono">{data.qr_id}</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
